import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X, MapPin, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'shopperden_exit_intent_dismissed';

/**
 * Exit-Intent Modal — fires once per visitor when the cursor races
 * toward the tab-close button (top of viewport). Capture-funnel for
 * abandoning traffic: email + city → drips them a free 1-page
 * snapshot that promotes the paid Growth Analysis.
 *
 * Rules:
 *  • Only triggers on desktop (mobile lacks the trigger surface).
 *  • Suppressed for 14 days after dismissal or capture (localStorage).
 *  • Never fires when an auth token exists — logged-in users have
 *    already converted past the cold-lead step.
 *  • Never fires on /login, /signup, /admin or /affiliate.
 */
const ExitIntentModal = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const armedRef = useRef(true);

  useEffect(() => {
    // Suppression guards
    const path = window.location.pathname;
    const blocked = ['/login', '/signup', '/admin', '/affiliate', '/buyer', '/seller', '/agent', '/tenant', '/professional', '/dashboard'];
    if (blocked.some(p => path.startsWith(p))) return;
    if (localStorage.getItem('token')) return;

    const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    if (dismissedAt && (Date.now() - dismissedAt) < 14 * 24 * 60 * 60 * 1000) return;

    // Desktop only — mouseleave-to-top is the classic exit signal.
    if (window.innerWidth < 900) return;

    const handler = (e) => {
      if (!armedRef.current) return;
      if (e.clientY <= 0 && e.relatedTarget == null) {
        armedRef.current = false;
        setOpen(true);
      }
    };
    document.addEventListener('mouseout', handler);
    return () => document.removeEventListener('mouseout', handler);
  }, []);

  const close = (markDismissed = true) => {
    setOpen(false);
    if (markDismissed) localStorage.setItem(STORAGE_KEY, String(Date.now()));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/sales/exit-intent`, {
        email: email.trim().toLowerCase(),
        city: city.trim() || null,
        source_url: window.location.href,
        country: localStorage.getItem('preferred_country') || null,
      });
      setSubmitted(true);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={() => close(true)}
      data-testid="exit-intent-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-modal"
      >
        <button
          onClick={() => close(true)}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 p-1"
          data-testid="exit-intent-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {!submitted ? (
          <>
            <div className="bg-gradient-to-br from-[#0F172A] to-[#1F3D2B] text-white px-7 py-6">
              <p className="text-[10px] tracking-[0.3em] text-[#F0A875] uppercase font-bold mb-2">
                Wait — one quick thing
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl leading-tight">
                Get a free 1-page property snapshot before you go.
              </h2>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">
                12-month prices · live rental yields · liquidity index — delivered to your inbox in 30 seconds.
              </p>
            </div>

            <form onSubmit={submit} className="px-7 py-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#E07A3A]"
                  data-testid="exit-intent-email"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1.5 flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" />City / area you're watching
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g. London, Mohali, Toronto"
                  className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#E07A3A]"
                  data-testid="exit-intent-city"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#E07A3A] hover:bg-[#C4652E] text-white tracking-wider text-xs uppercase font-bold py-5"
                data-testid="exit-intent-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Send my free snapshot
              </Button>

              <p className="text-[10px] text-slate-400 text-center pt-2">
                One-off email · no spam · reply STOP to opt out instantly.
              </p>
            </form>
          </>
        ) : (
          <div className="px-7 py-9 text-center" data-testid="exit-intent-success">
            <div className="w-14 h-14 mx-auto bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mb-4">
              <svg className="h-7 w-7 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A]">Snapshot on the way ✉️</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              We've just emailed your 1-page snapshot. Check spam if you don't see it in 60 seconds.
            </p>
            <Button
              onClick={() => close(false)}
              variant="outline"
              className="mt-5 text-xs uppercase tracking-wider"
              data-testid="exit-intent-success-close"
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitIntentModal;
